/**
 * 섹션 단독 미리보기 헬퍼.
 *
 * 문서 조립(전역 CSS + 스코프된 섹션 CSS + .wrapper 래핑)은 서버가 담당한다
 * — `POST /api/admin/section-designs/preview`. 스코프 함수(scopeSectionCss)가
 * api 전용이고 전역 CSS 가 수십 KB 라 클라이언트로 왕복시키지 않는다.
 * 여기에는 UI 안내 판정만 남긴다.
 */

/** 샘플 데이터(sample-invitation-data.ts)에서 enabledSections=true 인 sectionType 집합.
 *  이 목록에 없는 섹션은 `{% if enabledSections.X %}` 래퍼 때문에 미리보기가 비어 보일 수 있다. */
export const ENABLED_BY_SAMPLE = new Set<string>([
  'greeting', 'gallery', 'timeline', 'venue', 'directions', 'schedule',
  'contacts', 'accounts', 'rsvp', 'guestbook', 'notices', 'ending',
])

/**
 * 샘플 데이터에서 비활성이라 미리보기가 비어 보일 수 있는 섹션이면 안내 문구, 아니면 null.
 *
 * 경고의 전제는 HTML 이 `{% if enabledSections.X %}` 로 감싸여 있다는 것이다. 래퍼가 없으면
 * enabledSections 값과 무관하게 항상 렌더되므로 경고는 오진이다 — 그래서 htmlContent 를 함께 본다.
 * (예: cover 는 사용자가 켜고 끄는 섹션이 아니어서 샘플 enabledSections 에 키 자체가 없다)
 */
export function previewDisabledReason(sectionType: string, htmlContent = ''): string | null {
  if (ENABLED_BY_SAMPLE.has(sectionType)) return null
  if (!hasEnabledSectionsGuard(htmlContent, sectionType)) return null
  return `샘플 데이터에서 "${sectionType}" 섹션이 비활성이라 미리보기가 비어 보일 수 있습니다`
}

/** HTML 에 `{% if enabledSections.<sectionType> %}` 형태의 Liquid 가드가 있는지. */
function hasEnabledSectionsGuard(htmlContent: string, sectionType: string): boolean {
  if (!sectionType) return false
  // {%- if enabledSections.video -%} / {% unless enabledSections.video %} 등 변형 허용
  const re = new RegExp(
    `\\{%-?\\s*(?:if|unless|elsif)\\b[^%]*\\benabledSections\\.${escapeRegExp(sectionType)}\\b`,
  )
  return re.test(htmlContent)
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * 섹션 CSS 는 있는데 HTML 에 data-section 속성이 없는 상태인지 판정.
 *
 * 조립 시 섹션 CSS 는 `[data-section="X"]` 로 스코프되므로
 * (apps/api/src/lib/templates/css-scoper.ts), data-section 이 없으면 섹션 CSS 가
 * 미리보기에서도 실제 발행에서도 하나도 적용되지 않는다. 미리보기 고장으로
 * 오해하지 않도록 안내하기 위한 판정.
 */
export function needsDataSectionHint(input: { htmlContent: string; cssContent: string | null }): boolean {
  const { htmlContent, cssContent } = input
  if (!cssContent || !cssContent.trim()) return false
  return !/data-section\s*=/.test(htmlContent)
}
